/**
 * Parse a GitHub Flavored Markdown (GFM) table into an array of objects.
 * Used to extract recommendation data from AI-generated reports.
 */

export interface ParsedRow {
  [key: string]: string
}

/**
 * Parse a GFM table from markdown content.
 * Returns an array of objects where keys are column headers.
 *
 * Example input:
 * | Rank | Ticker | Strategy |
 * |------|--------|----------|
 * | 1    | AAPL   | CSP      |
 * | 2    | NVDA   | CC       |
 *
 * Returns:
 * [{ Rank: '1', Ticker: 'AAPL', Strategy: 'CSP' }, ...]
 */
export function parseGfmTable(markdown: string): ParsedRow[] {
  // Keep blank lines: a blank line is a paragraph boundary in GFM and MUST end the
  // table. (Previously all blank lines were filtered out up front, which let text
  // that happened to follow the table — e.g. a trailing "| ..." suffix after a
  // blank line — be mis-read as extra table rows. Trim per line but preserve
  // emptiness so the boundary survives.)
  const lines = markdown.split('\n').map(l => l.trim())

  // Find the table: a line starting with | followed by a separator line (|---|...)
  let headerIndex = -1
  for (let i = 0; i < lines.length - 1; i++) {
    if (lines[i].startsWith('|') && lines[i + 1].match(/^\|[\s-:|]+\|$/)) {
      headerIndex = i
      break
    }
  }

  if (headerIndex === -1) return []

  // Parse headers
  const headerLine = lines[headerIndex]
  const headers = parsePipeRow(headerLine)
  if (headers.length === 0) return []

  // Skip separator line
  const dataStartIndex = headerIndex + 2

  // Parse data rows — stop at the first blank line (paragraph break) or any line
  // that doesn't start with a pipe.
  const rows: ParsedRow[] = []
  for (let i = dataStartIndex; i < lines.length; i++) {
    const line = lines[i]
    if (line.length === 0) break // blank line ends the table
    if (!line.startsWith('|')) break // non-table line ends the table

    const values = parsePipeRow(line)
    if (values.length === 0) continue

    const row: ParsedRow = {}
    headers.forEach((header, idx) => {
      row[header] = values[idx] ?? ''
    })
    rows.push(row)
  }

  return rows
}

/**
 * Parse a single pipe-delimited row into cell values.
 * Handles leading/trailing pipes and trims whitespace.
 */
function parsePipeRow(line: string): string[] {
  // Remove leading/trailing pipes and split
  const trimmed = line.replace(/^\|/, '').replace(/\|$/, '')
  return trimmed.split('|').map(cell => cell.trim())
}

/**
 * Find and parse the first table with a "Rank" or "Ticker" column
 * from a markdown document (useful for recommendation reports).
 */
export function findRecommendationTable(markdown: string): ParsedRow[] {
  const sections = markdown.split(/\n(?=\|)/)

  for (const section of sections) {
    const rows = parseGfmTable(section)
    if (rows.length > 0) {
      const keys = Object.keys(rows[0]).map(k => k.toLowerCase())
      if (keys.includes('rank') || keys.includes('ticker') || keys.includes('#')) {
        return rows
      }
    }
  }

  // Fallback: try parsing the entire markdown
  return parseGfmTable(markdown)
}
